import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage } from "types/generated";

import { PollingIntervalGroups } from "../../../../apollo/constants";
import {
  TEMPLATES_LIMIT,
  TEMPLATES_LIMIT_STORAGE_KEY,
  FILTER_TYPE,
  initialSortDirection,
  initialSortOption,
} from "../constants";
import { SEARCH_TEMPLATES } from "../gql";
import useTemplatesCheck from "../useTemplatesCheck";

const useTemplates = () => {
  const [rowsPerPage] = useLocalStorage<number>(TEMPLATES_LIMIT_STORAGE_KEY);
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const { onError } = useTypedContext(FlashContext);

  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const resetPaginationVariables = useMemo(() => {
    return {
      fullTextSearch: filterVariables.searchInput,
      predicates: filterVariables.predicates,
      ...(filterVariables.sortOptionFields && { orderBy: filterVariables.sortOptionFields }),
    };
  }, [filterVariables]);

  const [previousResetPaginationVariables, setPreviousResetPaginationVariables] =
    useState(resetPaginationVariables);

  if (!isEqual(previousResetPaginationVariables, resetPaginationVariables)) {
    setPreviousResetPaginationVariables(resetPaginationVariables);
    setRequestedPage(null);
    setAfter(null);
  }

  const initialVariables = useMemo(() => {
    return {
      input: {
        ...resetPaginationVariables,
        first: rowsPerPage || TEMPLATES_LIMIT,
        after,
        requestedPage,
      },
    };
  }, [resetPaginationVariables, rowsPerPage, after, requestedPage]);

  const { checking, showUpSell } = useTemplatesCheck();

  const skipQuery = checking || showUpSell || !filterVariablesLoaded;

  const { error, loading, data, networkStatus, previousData, refetch } = usePolledQuery(
    SEARCH_TEMPLATES,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      // avoid request executing twice while fetchMore
      nextFetchPolicy: "cache-first",
      // APOLLO CLIENT UPDATE
      skip: skipQuery,
    }
  );

  const loadTemplates = useCallback(
    async ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      try {
        setAfter(cursor);
        setRequestedPage(requestedPage);
      } catch (error) {
        onError(error);
      }
    },
    [onError]
  );

  const initialLoading =
    checking ||
    (loading && networkStatus === NetworkStatus.loading && !data?.searchTemplates) ||
    !filterVariablesLoaded;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const templates = useMemo(
    () =>
      data?.searchTemplates?.edges.map((edge) => edge.node) ||
      previousData?.searchTemplates?.edges.map((edge) => edge.node) ||
      [],
    [data?.searchTemplates?.edges, previousData?.searchTemplates?.edges]
  );

  return {
    refetch,
    loadTemplates,
    error,
    templates,
    initialLoading,
    showUpSell,
    noResults: !loading && !data?.searchTemplates,
    endCursor: data?.searchTemplates?.pageInfo.endCursor,
    startCursor: data?.searchTemplates?.pageInfo.startCursor,
    isEmpty: data && !templates.length,
    loadingContent,
    predicates: filterVariables.predicates,
    paginationResetKey: resetPaginationVariables,
  };
};

export default useTemplates;
